import { ActionTree } from 'vuex';
import { EImportType } from '../../importer/types/enums/import-type';
import { ImporterHelper } from '../helpers/importer.helper';
import { EImportFilesStage } from '../types/enums/import-files-stage';
import { IImportFiles } from '../types/import-files';
import { IImportTemplate } from '../types/import-template';
import { EImportWordDocumentStage } from '../types/enums/import-word-document-stage';
import { IImportGoogleDrive } from '../types/import-google-drive';
import { EImportGoogleDriveStage } from '../types/enums/import-google-drive-stage';
import { EImporterAction, EImporterMutation, state as defaultState } from './importer.constants';
import { IImporterState } from './importer.types';
import { IProcessWordResponse } from '@/api/word/types/process-word-response';
import { IWordUploadResponse } from '@/api/word/types/word-upload-response';
import { DocumentService } from '@/api/documents/services/document.service';
import { UploadFileService } from '@/api/upload-files/services/upload-file.service';
import { IDocumentImportData } from '@/dealroom/types/document-import-data';
import { EExternalEditorType } from '@/enums/ExternalEditorType';
import { RootState } from '@/store/store';
import { IDocument } from '@/types/Document';
import { IDocumentPage } from '@/types/document-page';
import { IUploadFileUrl } from '@/types/upload-file-url';
import { WordService } from '@/api/word/services/word.service';
import { IWordMergeField } from '@/api/word/types/word-merge-field';
import { IWordMerge } from '@/dealroom/types/word-merge';
import { IWordTemplateResponse } from '@/api/word/types/word-template-response';
import { DealroomHelper } from '@/dealroom/helpers/dealroom.helper';
import { IGoogleUploadData } from '@/api/google/types/google-upload-data';
import { GoogleService } from '@/api/google/google.service';

export const actions: ActionTree<IImporterState, RootState> = {
  [EImporterAction.Reset]({ commit }) {
    commit(EImporterMutation.SetState, { ...defaultState });
  },

  [EImporterAction.ImportSuccess]({ commit }) {
    commit(EImporterMutation.SetImported, true);
  },

  [EImporterAction.ImportFailed]({ commit }, e: Error) {
    commit(EImporterMutation.SetError, e.toString());
    commit(EImporterMutation.SetImported, false);
  },

  [EImporterAction.PrepareImport](
    { commit, state },
    { title, fileCount, type }: { title: string; fileCount: number; type: EImportType }
  ) {
    commit(EImporterMutation.SetImported, false);
    commit(EImporterMutation.SetImportData, {
      ...state.importData,
      type,
      title,
    });
    commit(EImporterMutation.SetFileImportProperties, {
      fileCount,
    });
    commit(EImporterMutation.SetImporting, true);
  },

  async [EImporterAction.ImportTemplate]({ dispatch, commit }, payload: IImportTemplate) {
    const type: EImportType =
      payload.externalEditorType === EExternalEditorType.Docs
        ? EImportType.GoogleDocs
        : EImportType.Template;
    dispatch(EImporterAction.PrepareImport, {
      title: ImporterHelper.getImportingTemplateTitle(type, payload.name),
      fileCount: 1,
      type,
    });
    const { externalEditorType, documentId, templateId, pageIndex } = payload;
    try {
      const document: IDocument = await DocumentService.useTemplate(
        documentId,
        templateId,
        pageIndex
      );
      commit(EImporterMutation.SetImportedDocument, document);

      // Waiting for subscription to deliver pages
      if (externalEditorType === EExternalEditorType.Docs) {
        return;
      }
      dispatch(EImporterAction.ImportSuccess);
    } catch (e) {
      dispatch(EImporterAction.ImportFailed, e);
    }
  },

  async [EImporterAction.ImportWordDocument]({ dispatch, commit }, payload: IImportFiles) {
    const { files, documentId, pageIndex } = payload;
    dispatch(EImporterAction.PrepareImport, {
      title: ImporterHelper.getImportingWordDocumentTitle(files[0]),
      fileCount: 1,
      type: EImportType.WordDocument,
    });
    try {
      const response: IWordUploadResponse = await WordService.upload(files[0]);
      commit(EImporterMutation.SetImportStage, EImportWordDocumentStage.Uploaded);
      dispatch(EImporterAction.ProcessWordDocument, {
        documentId,
        pageIndex,
        missing: [],
        name: response.filename,
      });
    } catch (e) {
      dispatch(EImporterAction.ImportFailed, e);
    }
  },

  async [EImporterAction.ProcessWordDocument](
    { commit, dispatch },
    {
      documentId,
      pageIndex,
      missing,
      name,
    }: {
      documentId: string;
      pageIndex: number;
      missing: IWordMergeField[];
      name: string;
    }
  ) {
    const response: IProcessWordResponse = await WordService.process(
      documentId,
      name,
      missing,
      pageIndex
    );
    commit(EImporterMutation.SetImportStage, EImportWordDocumentStage.Processed);
    dispatch(EImporterAction.SetWordMergeProperties, {
      filename: response.filename,
      missing: response.missing,
      index: pageIndex,
    });
    // All ok, if status === 2 word merge fields can be filled in
    if (response.status === 1) {
      await WordService.saveImportLog(documentId);
      commit(EImporterMutation.SetImportStage, EImportWordDocumentStage.LogSaved);
    }
  },

  async [EImporterAction.SaveWordMergeDynamicTemplate](
    { commit, dispatch },
    {
      documentId,
      pageIndex,
      name,
    }: {
      documentId: string;
      pageIndex: number;
      name: string;
    }
  ) {
    try {
      dispatch(EImporterAction.SetWordMergeProperties, { missing: [] });
      const response: IWordTemplateResponse = await WordService.saveWordTemplate(
        documentId,
        name,
        pageIndex
      );
      if (response.status === 1) {
        await WordService.saveImportLog(documentId);
        commit(EImporterMutation.SetImportStage, EImportWordDocumentStage.LogSaved);
      }
    } catch (e) {
      dispatch(EImporterAction.ImportFailed, e);
    }
  },

  [EImporterAction.SetWordMergeProperties]({ commit, state }, properties: Partial<IWordMerge>) {
    commit(EImporterMutation.SetWordMerge, {
      ...state.wordMerge,
      ...properties,
    });
  },

  async [EImporterAction.ImportFiles]({ commit, dispatch }, payload: IImportFiles) {
    dispatch(EImporterAction.PrepareImport, {
      title: ImporterHelper.getImportingFilesTitle(payload.files),
      fileCount: payload.files.length,
      type: EImportType.File,
    });
    const { documentId, files, pageIndex } = payload;
    const fileNames: string[] = files.map((file: File) => file.name);
    try {
      const uploadFileURLs: IUploadFileUrl[] = await UploadFileService.getUploadFilesUrls(
        documentId,
        fileNames
      );
      commit(EImporterMutation.SetImportStage, EImportFilesStage.URLsRecieved);

      await UploadFileService.uploadFiles(uploadFileURLs, files);
      const uniqueFileNames: string[] = uploadFileURLs.map(
        ({ uniqueFilename }: IUploadFileUrl) => uniqueFilename
      );
      commit(EImporterMutation.SetImportStage, EImportFilesStage.Uploading);

      await UploadFileService.uploadFilesDone(documentId, pageIndex, uniqueFileNames);
      commit(EImporterMutation.SetImportStage, EImportFilesStage.Uploaded);
    } catch (e) {
      dispatch(EImporterAction.ImportFailed, e);
    }
  },

  [EImporterAction.StartLoadingPages]({ commit, state }, payload: IDocumentImportData) {
    commit(EImporterMutation.SetFileImportProperties, {
      pageCount: state.fileImport.pageCount + payload.totalCount,
      recievedFileImportCount: ++state.fileImport.recievedFileImportCount,
    });
    if (state.importData.stage !== EImportFilesStage.ThumbnailImportStarted) {
      commit(EImporterMutation.SetImportStage, EImportFilesStage.ThumbnailImportStarted);
    }
  },

  async [EImporterAction.LoadPage](
    { commit, state, dispatch },
    {
      documentId,
      id,
      isGoogleDocTemplate,
    }: { documentId: string; id: string; isGoogleDocTemplate: boolean }
  ) {
    try {
      const page: IDocumentPage = await DocumentService.loadDocumentPage(documentId, id);
      commit(EImporterMutation.SetImportedPages, [...state.importedPages, page]);
      if (!ImporterHelper.pageImportDone(state.fileImport, state.importedPages?.length)) {
        return;
      }

      // We need to manually fetch Google docs templates since it can contain editor, video or content link blocks
      if (isGoogleDocTemplate) {
        const pages: IDocumentPage[] = await DocumentService.loadPages(documentId);
        commit(EImporterMutation.SetImportedPages, pages);
      }

      dispatch(EImporterAction.CompleteFileImport, documentId);
    } catch (e) {
      dispatch(EImporterAction.ImportFailed, e);
    }
  },

  async [EImporterAction.CompleteFileImport]({ dispatch, state, commit }, documentId: string) {
    try {
      const correctedPageNumbers: Partial<IDocumentPage>[] = await DocumentService.loadPageNums(
        documentId
      );
      const updatedPages: IDocumentPage[] = DealroomHelper.updatePageNums(
        state.importedPages,
        correctedPageNumbers
      );
      commit(EImporterMutation.SetImportedPages, updatedPages);
      dispatch(EImporterAction.ImportSuccess);
    } catch (e) {
      dispatch(EImporterAction.ImportFailed);
    }
  },

  async [EImporterAction.ImportGoogleDrive]({ dispatch, commit }, payload: IImportGoogleDrive) {
    dispatch(EImporterAction.PrepareImport, {
      title: ImporterHelper.getImportingGoogleDriveTitle(payload.fileName),
      fileCount: 1,
      type: EImportType.GoogleDrive,
    });
    try {
      const data: IGoogleUploadData = await GoogleService.upload(payload.fileName, payload.fileUrl);
      commit(EImporterMutation.SetImportStage, EImportGoogleDriveStage.Uploaded);
      if (!data.success) {
        dispatch(EImporterAction.ImportFailed);
      } else {
        await GoogleService.save(data.filename, payload.documentId, payload.pageIndex);
        commit(EImporterMutation.SetImportStage, EImportGoogleDriveStage.Saved);
        await GoogleService.saveImportLog(payload.documentId);
        commit(EImporterMutation.SetImportStage, EImportGoogleDriveStage.LogSaved);
      }
    } catch (e) {
      dispatch(EImporterAction.ImportFailed, e);
    }
  },
};
